import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { MapPin, MapPinOff, Loader2, RefreshCw, Crosshair } from "lucide-react";
import { useUserLocation, placeLabel } from "@/lib/location";
import { Row } from "@/components/common";

export function LocationChip() {
  const { status, coords, place, request, error } = useUserLocation();
  const granted = status === "granted" && coords;
  const tone = granted ? "border-emerald-200 bg-emerald-50 text-emerald-800"
    : status === "denied" || status === "error" ? "border-rose-200 bg-rose-50 text-rose-800" : "border-amber-200 bg-amber-50 text-amber-900";
  const label = granted ? (place?.city || "Ubicación activa") + ` · ±${coords.accuracy} m`
    : status === "locating" ? "Obteniendo GPS…" : status === "denied" ? "Ubicación bloqueada" : status === "unsupported" ? "Sin GPS" : "Activar ubicación";
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button className={`hidden md:inline-flex items-center gap-1.5 h-9 rounded-lg border px-2.5 text-xs font-medium transition-colors ${tone}`} data-testid="location-chip">
          {status === "locating" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : granted ? <span className="live-dot h-2 w-2 rounded-full bg-[#1F6B4A]" /> : status === "denied" ? <MapPinOff className="h-3.5 w-3.5" /> : <MapPin className="h-3.5 w-3.5" />}
          <span className="max-w-[180px] truncate" data-testid="location-chip-label">{label}</span>
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 bg-white" data-testid="location-popover">
        <div className="flex items-center gap-2 font-medium text-sm"><Crosshair className="h-4 w-4 text-primary" />Tu ubicación exacta</div>
        {granted ? (
          <div className="mt-3 space-y-2 text-sm">
            <div className="rounded-md bg-secondary p-2 text-xs" data-testid="location-address">{placeLabel(place) || "Buscando dirección…"}</div>
            <Row label="Coordenadas"><span className="font-mono text-xs" data-testid="location-coords">{coords.lat.toFixed(6)}, {coords.lng.toFixed(6)}</span></Row>
            <Row label="Precisión"><span className="text-xs">±{coords.accuracy} m</span></Row>
            {place?.municipio && <Row label="Municipio"><span className="text-xs">{place.municipio}</span></Row>}
            {place && !place.in_qroo && <div className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded p-2">Estás fuera de Quintana Roo: los reportes deben ubicarse dentro del estado.</div>}
            <Row label="Actualizado"><span className="text-xs">{coords.at.toLocaleTimeString("es-MX")}</span></Row>
          </div>
        ) : status === "denied" ? (
          <div className="mt-3 text-sm space-y-2">
            <p className="text-muted-foreground">Bloqueaste el acceso. Para activarlo: toca el ícono de candado junto a la dirección del sitio → <b>Ubicación</b> → <b>Permitir</b>, y recarga la página.</p>
          </div>
        ) : (
          <p className="mt-3 text-sm text-muted-foreground">{error || "Usamos tu GPS para ubicar tus reportes con precisión, encontrar incidencias cercanas y coordinar cuadrillas."}</p>
        )}
        <Button size="sm" variant={granted ? "outline" : "default"} className="w-full mt-3" onClick={request} data-testid="location-request-button">
          <RefreshCw className="h-3.5 w-3.5 mr-1" />{granted ? "Actualizar ubicación" : "Permitir ubicación"}
        </Button>
      </PopoverContent>
    </Popover>
  );
}

export function LocationBanner() {
  const { status, request } = useUserLocation();
  if (!["denied", "error", "prompt"].includes(status)) return null;
  return (
    <div className={`border-b px-4 md:px-6 py-2 text-sm flex flex-wrap items-center gap-2 ${status === "prompt" ? "bg-amber-50 border-amber-200 text-amber-900" : "bg-rose-50 border-rose-200 text-rose-800"}`} data-testid="location-banner">
      <MapPin className="h-4 w-4" />
      <span className="flex-1 min-w-[200px]">
        {status === "prompt" ? "HitchGob necesita tu ubicación exacta para geolocalizar reportes y servicios cercanos. Acepta el permiso del navegador."
          : status === "denied" ? "La ubicación está bloqueada en tu navegador. Actívala desde el candado de la barra de direcciones y recarga."
          : "No pudimos obtener tu GPS. Verifica que la ubicación de tu dispositivo esté encendida."}
      </span>
      <Button size="sm" variant="outline" className="bg-white h-8" onClick={request} data-testid="location-banner-button">Permitir ubicación</Button>
    </div>
  );
}
